import {useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties} from 'react';
import {useAsaTracking, useCioAsaContext} from '@constructor-io/constructorio-ui-asa';
import {ChevronLeft, ChevronRight} from 'lucide-react';
import {adaptProduct, safeUrl} from '../constructor/productAdapter';
import {originalGroupTitle, visualGroupTitle, type TweChatMessage, type TweResultGroup} from '../constructor/assistantModel';
import {buildDefaultResultsUrl} from '../constructor/resultsUrl';
import type {EventBus} from '../host/eventBus';
import type {AsaResultRequest, EventContext} from '../host/types';
import {ProductCard} from './ProductCard';
import {AssistantMarkdown} from './AssistantMarkdown';
import {resolveAgentContent, resolveContentTemplate} from '../content';
import {scrollProductRail} from './productRailScroll';

export function ProductGroup({group, message, index, bus, paired = false, productIndex}: {group: TweResultGroup; message: TweChatMessage; index: number; bus: EventBus; paired?: boolean; productIndex?: number}) {
  const content = resolveAgentContent().products;
  const cio = useCioAsaContext();
  const tracking = useAsaTracking({tracker: cio?.cioClient?.tracker, section: cio?.section, threadId: message.threadId});
  const trackingRef = useRef(tracking);
  trackingRef.current = tracking;
  const strip = useRef<HTMLDivElement>(null);
  const container = useRef<HTMLElement>(null);
  const [navigation, setNavigation] = useState({overflow: false, left: false, right: false});
  const [viewingMore, setViewingMore] = useState(false);
  // Temporarily hidden pending clarification from Constructor.
  const showViewMoreProducts = false;
  const title = originalGroupTitle(group);
  const heading = visualGroupTitle(group);
  const step = /^\d+$/.test(heading);
  const label = title.trim() || heading || content.recommendedProductsAriaLabel;
  const description = typeof group.description === 'string' ? group.description.trim() : '';
  const key = `${message.id}:${message.renderAttempt || 0}:${index}:${group.searchResultId || 'result'}`;
  const request = group.group?.data?.request as AsaResultRequest | undefined;
  const products = useMemo(() => group.searchResults.map(product => adaptProduct(product, bus.options.config?.currency || 'GBP')), [group.searchResults, bus]);
  const productEntries = useMemo(() => products.map((product,sourceIndex)=>({product,sourceIndex})).filter(entry=>productIndex === undefined || entry.sourceIndex === productIndex), [products,productIndex]);
  const context = useMemo<EventContext>(() => ({
    threadId: message.threadId,
    intentResultId: group.intentResultId || message.intentResultId,
    searchResultId: group.searchResultId,
    groupId: group.searchResultId,
    originalPrompt: message.intent,
    groupTitle: title,
    source: 'conversation',
  }), [message.threadId, message.intentResultId, message.intent, group.intentResultId, group.searchResultId, title]);

  useLayoutEffect(() => {
    const element = strip.current;
    if (!element) return;
    const measure = () => {
      const overflow = element.scrollWidth > element.clientWidth + 1;
      const left = overflow && element.scrollLeft > 1;
      const right = overflow && element.scrollLeft + element.clientWidth < element.scrollWidth - 1;
      setNavigation(previous => previous.overflow === overflow && previous.left === left && previous.right === right ? previous : {overflow, left, right});
    };
    measure();
    element.addEventListener('scroll', measure, {passive: true});
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(element);
    for (const child of Array.from(element.children)) observer?.observe(child);
    return () => {
      element.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [productEntries]);

  useEffect(() => {
    const element = container.current;
    if (!element) return;
    const shown = () => bus.once(`group:${key}`, () => {
      const publicGroup = {title, products, searchResultId: group.searchResultId, ...(request && {request})};
      bus.emit('onProductGroupDisplayed', {
        group: publicGroup,
        groupId: group.searchResultId,
        groupTitle: title,
        productCount: products.length,
        totalProductCount: products.length,
      }, context);
    });
    if (!('IntersectionObserver' in window)) {
      shown();
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        shown();
        observer.disconnect();
      }
    }, {threshold: .1});
    observer.observe(element);
    return () => observer.disconnect();
  }, [bus, key, products, context, group.searchResultId, title, request]);

  // Track only cards crossing the same visibility threshold as ProductCard.
  // All returned cards are mounted in one horizontal strip, but clipped cards
  // remain unreported until scrolling moves them into view.
  useEffect(() => {
    const element = strip.current;
    if (!element || !message.intent || !group.searchResultId) return;
    const shown = (nodes: Element[]) => {
      const newlyVisible: typeof products = [];
      nodes.forEach(node => {
        const position = Number((node as HTMLElement).dataset.position);
        if (products[position]) bus.once(`constructor-view:${key}:${position}`, () => newlyVisible.push(products[position]));
      });
      if (!newlyVisible.length) return;
      try {
        trackingRef.current.trackResultView({intent: message.intent!, searchResultId: group.searchResultId!, intentResultId: context.intentResultId,
          numResultsViewed: newlyVisible.length, items: newlyVisible.map(product => ({itemId: product.id, itemName: product.name, variationId: product.variationId,
            ...(product.price != null && Number.isFinite(Number(product.price)) && {price: Number(product.price)})}))});
      } catch (error) { bus.error(error, context, 'CONSTRUCTOR_TRACKING_FAILED', 'product_interaction'); }
    };
    // Do not guess viewport visibility if the browser lacks IntersectionObserver.
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(entries => {
      const visible = entries.filter(entry => entry.isIntersecting && entry.intersectionRatio >= .2);
      shown(visible.map(entry => entry.target));
      visible.forEach(entry => observer.unobserve(entry.target));
    }, {threshold: .2});
    Array.from(element.children).forEach(child => observer.observe(child));
    return () => observer.disconnect();
  }, [bus, key, products, productEntries, message.intent, group.searchResultId, context]);

  const viewMore = async () => {
    if (!request || viewingMore) return;
    const viewMoreContext: EventContext = {...context, interaction: 'view-more'};
    setViewingMore(true);
    bus.emit('onViewMoreClick', {
      searchTerm: request.term,
      userInput: message.intent,
      groupId: group.searchResultId,
      groupTitle: title,
      constructorRequest: request,
      filters: request.filters,
      browse: request.browse_filter_name || request.browse_filter_value ? {
        name: request.browse_filter_name,
        value: request.browse_filter_value,
      } : undefined,
      sorting: request.sort_by || request.sort_order ? {by: request.sort_by, order: request.sort_order} : undefined,
    }, viewMoreContext);
    try {
      const value = bus.options.actions?.buildResultsUrl?.(request, viewMoreContext) ?? buildDefaultResultsUrl(request);
      const url = safeUrl(value);
      if (!url) throw new Error('Host buildResultsUrl must return an http(s) URL.');
      if (bus.options.actions?.navigateToResults) await bus.options.actions.navigateToResults(url, request, viewMoreContext);
      else window.location.assign(url);
    } catch (error) {
      bus.error(error, viewMoreContext, 'VIEW_MORE_FAILED', 'view_more');
      setViewingMore(false);
    }
  };

  const controls = navigation.overflow && <div className="twe-drinks-advisor__strip-controls"><button type="button" aria-label={resolveContentTemplate(content.scrollLeftAriaLabel, {GroupTitle: label})} disabled={!navigation.left} onClick={() => scrollProductRail(strip.current, -1)}><ChevronLeft size={17}/></button><button type="button" aria-label={resolveContentTemplate(content.scrollRightAriaLabel, {GroupTitle: label})} disabled={!navigation.right} onClick={() => scrollProductRail(strip.current, 1)}><ChevronRight size={17}/></button></div>;
  return <section ref={container} className={`twe-drinks-advisor__product-group${productEntries.length === 1 ? ' twe-drinks-advisor__product-group--singleton' : ''}${paired ? ' twe-drinks-advisor__product-group--paired' : ''}`} aria-label={label} data-group-id={group.searchResultId} data-content-type="search_result" data-total-products={productEntries.length} style={{'--group-reveal-delay': '0ms'} as CSSProperties}>
    {!paired && (heading ? <div className="twe-drinks-advisor__group-heading">{step ? <p className="twe-drinks-advisor__recommendation-label twe-drinks-advisor__recommendation-label--step" aria-label={`Recommendation ${heading}`}>{heading}</p> : <h3>{heading}</h3>}{controls}</div> : controls ? <div className="twe-drinks-advisor__group-navigation">{controls}</div> : null)}
    {!paired && description && <AssistantMarkdown text={description} className="twe-drinks-advisor__group-description"/>}
    <div ref={strip} className="twe-drinks-advisor__product-strip" role="list" aria-label={label}>{productEntries.map(({product, sourceIndex: position}) => <div role="listitem" data-position={position} key={`${product.id}:${position}`} style={{'--card-reveal-delay': '0ms'} as CSSProperties}><ProductCard product={product} context={{...context, position: position + 1}} bus={bus} displayKey={`product:${key}:${position}`} trackClick={() => {
      if (message.intent && group.searchResultId) tracking.trackResultClick({intent: message.intent, searchResultId: group.searchResultId, intentResultId: context.intentResultId, itemId: product.id, itemName: product.name, variationId: product.variationId});
    }}/></div>)}</div>
    {showViewMoreProducts && request && <div className="twe-drinks-advisor__view-more"><button type="button" className="cta-button cta-button--delta cta-button--regular" disabled={viewingMore} onClick={() => { void viewMore(); }}>{content.viewMoreProducts}</button></div>}
  </section>;
}
